'use client';

/**
 * Accordion Component
 *
 * A component that displays content in collapsible sections.
 * Uses shadcn/ui accordion primitives internally.
 */
import React, { useState } from 'react';

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Accordion as ShadcnAccordionRoot
} from '../../ui/accordion';

export interface AccordionItem {
  title: string;
  subtitle?: string;
  content: React.ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  defaultOpenIndex?: number | null;
  variant?: 'default' | 'bordered';
}

/**
 * Accordion component for displaying content in collapsible sections
 *
 * @example
 * ```tsx
 * <Accordion
 *   items={[
 *     { title: 'What is TopTopTime?', content: 'TopTopTime is a platform...' },
 *     { title: 'How do I sign up?', content: 'You can sign up by...' }
 *   ]}
 *   defaultOpenIndex={0}
 * />
 * ```
 */
export const Accordion: React.FC<AccordionProps> = ({ items, defaultOpenIndex = null, variant = 'default' }) => {
  // Convert defaultOpenIndex to the format expected by shadcn accordion
  const defaultValue = defaultOpenIndex !== null ? `item-${defaultOpenIndex}` : undefined;

  // State to track the open item
  const [openItem, setOpenItem] = useState<string | undefined>(defaultValue);

  // Handle accordion change
  const handleValueChange = (value: string) => {
    setOpenItem(value);
  };

  return (
    <ShadcnAccordionRoot
      type="single"
      collapsible
      value={openItem}
      onValueChange={handleValueChange}
      className="w-full"
    >
      {items.map((item, index) => {
        // Generate an id based on title
        const itemId = `item-${item.title.toLowerCase().replace(/\s+/g, '-')}-${index}`;
        return (
          <AccordionItem
            key={itemId}
            value={`item-${index}`}
            className={variant === 'bordered' ? 'border border-neutral-200 rounded-lg mb-3 last:mb-0' : undefined}
          >
            <AccordionTrigger
              className={
                variant === 'bordered'
                  ? `py-4 px-4 cursor-pointer ${item.subtitle ? 'items-start' : ''}`
                  : 'py-7 cursor-pointer'
              }
            >
              <div className="text-left">
                <div>{item.title}</div>
                {item.subtitle && <div className="text-xs text-neutral-500 mt-1">{item.subtitle}</div>}
              </div>
            </AccordionTrigger>
            <AccordionContent className={variant === 'bordered' ? 'px-4' : undefined}>{item.content}</AccordionContent>
          </AccordionItem>
        );
      })}
    </ShadcnAccordionRoot>
  );
};
